import { z } from "zod";
import { PERMISSIONS, ROLES } from "./permissions";

/**
 * Schémas d'entrée de l'API (validés côté serveur, réutilisés par la PWA pour
 * ses formulaires). Les types de sortie sont dans `dto.ts`.
 */

export const periodKeySchema = z.enum(["jour", "semaine", "mois", "annee"]);
export const compareSchema = z.enum(["j7", "m1", "n1"]);

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** Paramètres de période communs aux lectures de ventes. */
export const periodQuerySchema = z.object({
  period: periodKeySchema.default("jour"),
  cmp: compareSchema.default("j7"),
  /** Jour d'ancrage AAAA-MM-JJ (défaut : aujourd'hui). */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /**
   * Plage libre AAAA-MM-JJ, bornes incluses (prioritaire sur `period`/`date`
   * quand elle est valide : fin ≥ début, 731 jours au plus), avec en option une
   * plage de comparaison libre `cmpFrom`/`cmpTo` (sinon : les N jours d'avant).
   */
  from: isoDay.optional(),
  to: isoDay.optional(),
  cmpFrom: isoDay.optional(),
  cmpTo: isoDay.optional(),
  /** Affiner sur un sous-ensemble de boutiques (toujours borné au périmètre). */
  boutiqueIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v == null ? undefined : (Array.isArray(v) ? v : v.split(",")).filter(Boolean))),
});
export type PeriodQuery = z.infer<typeof periodQuerySchema>;

// --- Comptes Hiboutik & boutiques ---

const subdomain = z
  .string()
  .trim()
  .min(1, "Sous-domaine Hiboutik requis")
  .max(120)
  .regex(/^[a-z0-9-]+$/i, "Sous-domaine invalide (ex. « maboutique » pour maboutique.hiboutik.com)");

export const hiboutikAccountCreateSchema = z.object({
  label: z.string().trim().min(1, "Nom requis").max(120),
  subdomain,
  apiUser: z.string().trim().min(1, "Identifiant API requis").max(200),
  apiKey: z.string().trim().min(1, "Clé API requise").max(400),
});
export const hiboutikAccountUpdateSchema = hiboutikAccountCreateSchema.partial();

/**
 * Champs d'une boutique SANS valeurs par défaut : avec zod 4, `.partial()`
 * conserve les défauts, et une mise à jour partielle réécrirait alors le type,
 * la position ou l'état actif de la boutique.
 */
const boutiqueFields = z.object({
  name: z.string().trim().min(1, "Nom requis").max(120),
  city: z.string().trim().min(1, "Ville requise").max(120),
  type: z.enum(["INTEGRATED", "FRANCHISE"]),
  hiboutikAccountId: z.string().min(1, "Compte Hiboutik requis"),
  warehouseId: z.coerce.number().int().positive(),
  shopId: z.coerce.number().int().positive().nullable().optional(),
  openingHours: z.string().max(60).nullable().optional(),
  reportEmails: z.string().max(1000).nullable().optional(),
  monthlyRevenueTarget: z.coerce.number().positive().nullable().optional(),
  position: z.coerce.number().int().min(0),
  active: z.boolean(),
  minStaff: z.coerce.number().int().min(0).max(30),
});

export const boutiqueCreateSchema = boutiqueFields.extend({
  type: boutiqueFields.shape.type.default("FRANCHISE"),
  warehouseId: boutiqueFields.shape.warehouseId.default(1),
  position: boutiqueFields.shape.position.default(0),
  active: boutiqueFields.shape.active.default(true),
  minStaff: boutiqueFields.shape.minStaff.default(1),
});
export const boutiqueUpdateSchema = boutiqueFields.partial();

// --- Accès ---

export const roleSchema = z.enum(ROLES);
export const permissionSchema = z.enum(PERMISSIONS);

export const accessScopeSchema = z.object({
  hiboutikAccountIds: z.array(z.string()).default([]),
  boutiqueIds: z.array(z.string()).default([]),
});

export const invitationCreateSchema = accessScopeSchema.extend({
  email: z.string().trim().toLowerCase().email("E-mail invalide"),
  role: roleSchema.exclude(["OWNER"]),
  grantedPermissions: z.array(permissionSchema).default([]),
  revokedPermissions: z.array(permissionSchema).default([]),
});

export const memberUpdateSchema = z.object({
  role: roleSchema.exclude(["OWNER"]).optional(),
  grantedPermissions: z.array(permissionSchema).optional(),
  revokedPermissions: z.array(permissionSchema).optional(),
  hiboutikAccountIds: z.array(z.string()).optional(),
  boutiqueIds: z.array(z.string()).optional(),
});

export const organizationCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
});

// --- Destinataires (messages, tâches, annonces, campagnes) ---

export const targetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("boutiques"), boutiqueIds: z.array(z.string()).min(1, "Choisir au moins une boutique") }),
  z.object({ kind: z.literal("group"), groupId: z.string().min(1) }),
  z.object({ kind: z.literal("network") }),
]);
export type Target = z.infer<typeof targetSchema>;

// --- Messagerie ---

export const messageCreateSchema = z.object({
  body: z.string().trim().min(1, "Message vide").max(10_000),
  attachmentIds: z.array(z.string()).max(10).default([]),
});

export const messagesQuerySchema = z.object({
  after: z.string().optional(),
  before: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(40),
});

export const conversationFilterSchema = z.enum(["tous", "non_lus", "taches", "retard"]);

// --- Tâches ---

export const TASK_TYPES = ["PROMO", "POSTER", "DISPLAY", "RESTOCK", "ARRIVAL", "FREE"] as const;
export const TASK_PRIORITIES = ["LOW", "NORMAL", "HIGH"] as const;
export const TASK_STATUSES = ["SENT", "SEEN", "IN_PROGRESS", "DONE", "VALIDATED", "REFUSED"] as const;

export const taskCreateSchema = z.object({
  target: targetSchema,
  title: z.string().trim().min(1, "Titre requis").max(200),
  description: z.string().max(5000).optional().nullable(),
  type: z.enum(TASK_TYPES).default("FREE"),
  priority: z.enum(TASK_PRIORITIES).default("NORMAL"),
  dueAt: z.coerce.date().optional().nullable(),
  requiresProof: z.boolean().default(false),
  requiresInvoice: z.boolean().default(false),
  attachmentIds: z.array(z.string()).max(10).default([]),
});

export const assignmentActionSchema = z.discriminatedUnion("action", [
  z.object({ action: z.literal("seen") }),
  z.object({ action: z.literal("start") }),
  /** Joint une photo / une facture à la tâche, sans la marquer faite. */
  z.object({ action: z.literal("proof"), proofAttachmentIds: z.array(z.string()).min(1).max(10) }),
  z.object({ action: z.literal("done"), proofAttachmentIds: z.array(z.string()).max(10).default([]) }),
  z.object({ action: z.literal("validate") }),
  z.object({ action: z.literal("refuse"), reason: z.string().max(1000).optional() }),
  z.object({ action: z.literal("reopen") }),
]);

// --- Annonces ---

export const announcementCreateSchema = z.object({
  target: targetSchema,
  title: z.string().trim().min(1, "Titre requis").max(200),
  body: z.string().max(10_000).optional().default(""),
  attachmentIds: z.array(z.string()).max(10).default([]),
});

/** Formulaire unique « Nouveau message / tâche / annonce » (tiroir du design). */
export const composeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("message"),
    target: targetSchema,
    title: z.string().max(200).optional().default(""),
    body: z.string().max(10_000).optional().default(""),
    attachmentIds: z.array(z.string()).max(10).default([]),
  }),
  taskCreateSchema.extend({ kind: z.literal("tache") }),
  announcementCreateSchema.extend({ kind: z.literal("annonce") }),
]);
export type ComposeInput = z.input<typeof composeSchema>;

// --- Groupes ---

export const groupUpsertSchema = z.object({
  name: z.string().trim().min(1).max(80),
  boutiqueIds: z.array(z.string()).default([]),
});

// --- Campagnes ---

export const campaignCreateSchema = z
  .object({
    target: targetSchema,
    name: z.string().trim().min(1, "Titre requis").max(120),
    /** L'offre se décrit librement (« 1 produit au choix offert », « 3 achetés = 1 offert »…). */
    description: z.string().max(5000).optional().nullable(),
    /** Remise indicative facultative (anciennes campagnes). */
    discountPct: z.coerce.number().min(0).max(100).optional().nullable(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    productNames: z.array(z.string().trim().min(1)).default([]),
    /** Crée automatiquement les tâches « affiche » et « mise en place » dans chaque boutique. */
    createTasks: z.boolean().default(true),
    /** Fichiers joints (visuels, affiches, PDF), téléversés au préalable. */
    attachmentIds: z.array(z.string()).max(20).default([]),
  })
  .refine((c) => c.endsAt >= c.startsAt, { message: "La fin doit suivre le début", path: ["endsAt"] });

export const campaignUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().max(5000).optional().nullable(),
  discountPct: z.coerce.number().min(0).max(100).optional().nullable(),
  startsAt: z.coerce.date().optional(),
  endsAt: z.coerce.date().optional(),
  productNames: z.array(z.string().trim().min(1)).optional(),
  boutiqueIds: z.array(z.string()).optional(),
  /** Fichiers à ajouter (téléversés au préalable). */
  addAttachmentIds: z.array(z.string()).max(20).default([]),
  /** Fichiers à retirer de la campagne. */
  removeAttachmentIds: z.array(z.string()).max(50).default([]),
});

// --- Stock ---

export const promoApplySchema = z.object({
  productId: z.coerce.number().int().positive(),
  promoPct: z.coerce.number().gt(0).lt(100),
});

// --- Web Push ---

export const pushSubscriptionSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }),
});

// --- Plateforme ---

export const platformNetworkCreateSchema = z.object({
  name: z.string().trim().min(1, "Nom requis").max(120),
  /** Propriétaire à inviter (lien d'invitation renvoyé). */
  ownerEmail: z.string().trim().toLowerCase().email("E-mail invalide").optional().or(z.literal("")),
});

export const platformInvitationSchema = z.object({
  email: z.string().trim().toLowerCase().email("E-mail invalide"),
  role: z.enum(["OWNER", "ADMIN"]),
});

export const platformAdminGrantSchema = z.object({
  email: z.string().trim().toLowerCase().email("E-mail invalide"),
});

// --- Planning des équipes ---

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Heure invalide (HH:MM)");
const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date invalide (AAAA-MM-JJ)");

export const planningQuerySchema = z.object({
  /** Premier jour affiché (AAAA-MM-JJ) ; défaut : lundi de la semaine courante. */
  from: ymd.optional(),
  days: z.coerce.number().int().min(1).max(42).default(7),
  boutiqueIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => (v == null ? undefined : (Array.isArray(v) ? v : v.split(",")).filter(Boolean))),
});

const staffFields = z.object({
  boutiqueId: z.string().min(1, "Boutique requise"),
  name: z.string().trim().min(1, "Nom requis").max(120),
  jobTitle: z.string().trim().max(80).optional().nullable(),
  email: z.string().trim().toLowerCase().email("E-mail invalide").optional().nullable().or(z.literal("")),
  phone: z.string().trim().max(40).optional().nullable(),
  contractHours: z.coerce.number().min(0).max(80).optional().nullable(),
  color: z.string().max(40).optional().nullable(),
  /** Lier au compte (même e-mail) pour que la personne voie ses horaires. */
  linkAccount: z.boolean(),
  active: z.boolean(),
});
export const staffMemberSchema = staffFields.extend({
  linkAccount: staffFields.shape.linkAccount.default(true),
  active: staffFields.shape.active.default(true),
});
export const staffImportSchema = z.object({
  boutiqueId: z.string().min(1),
  personnes: z
    .array(z.object({ externalSource: z.enum(["HIBOUTIK", "EASYSHOP"]), externalId: z.string().min(1).max(64) }))
    .min(1, "Choisissez au moins une personne")
    .max(200),
});

export const staffMemberUpdateSchema = staffFields.partial();

export const shiftSchema = z
  .object({
    staffMemberId: z.string().min(1, "Personne requise"),
    boutiqueId: z.string().min(1, "Boutique requise"),
    date: ymd,
    start: hhmm,
    end: hhmm,
    breakMinutes: z.coerce.number().int().min(0).max(240).default(0),
    note: z.string().max(300).optional().nullable(),
  })
  .refine((s) => s.end > s.start, { message: "La fin doit suivre le début", path: ["end"] });

export const shiftUpdateSchema = z.object({
  staffMemberId: z.string().optional(),
  boutiqueId: z.string().optional(),
  date: ymd.optional(),
  start: hhmm.optional(),
  end: hhmm.optional(),
  breakMinutes: z.coerce.number().int().min(0).max(240).optional(),
  note: z.string().max(300).optional().nullable(),
});

export const copyWeekSchema = z.object({
  boutiqueId: z.string().min(1),
  /** Lundi de la semaine source et de la semaine cible. */
  fromWeek: ymd,
  toWeek: ymd,
  /** Remplacer les créneaux déjà présents dans la semaine cible. */
  replace: z.boolean().default(false),
});

export const ABSENCE_TYPES = ["LEAVE", "SICK", "TRAINING", "OTHER"] as const;

export const absenceSchema = z
  .object({
    staffMemberId: z.string().min(1),
    type: z.enum(ABSENCE_TYPES).default("LEAVE"),
    startDate: ymd,
    endDate: ymd,
    note: z.string().max(300).optional().nullable(),
  })
  .refine((a) => a.endDate >= a.startDate, { message: "La fin doit suivre le début", path: ["endDate"] });

// --- Administration plateforme ----------------------------------------------

export const adminInitialisationSchema = z.object({
  hiboutikAccountId: z.string().min(1),
  options: z
    .object({
      siren: z
        .string()
        .trim()
        .transform((v) => v.replace(/\s/g, ""))
        .refine((v) => v === "" || /^\d{9}$/.test(v), "Le SIREN compte 9 chiffres")
        .transform((v) => (v === "" ? null : v))
        .nullish(),
      magasins: z.array(z.number().int().positive()).nullish(),
      depuis: z
        .string()
        .regex(/^\d{4}-\d{2}$/, "Mois attendu au format AAAA-MM")
        .nullish()
        .or(z.literal("").transform(() => null)),
      ouvrirFiches: z.boolean().optional(),
      moisActifs: z.number().int().min(0).max(240).optional(),
      moisArrivages: z.number().int().min(0).max(240).optional(),
      joursVendeurs: z.number().int().min(1).max(730).optional(),
    })
    .default({}),
});

export const adminEasyshopLinkSchema = z.discriminatedUnion("mode", [
  /** Une boutique existante du tableau de bord passe sur les chiffres de la caisse. */
  z.object({ mode: z.literal("existing"), tenantId: z.string().min(1), boutiqueId: z.string().min(1) }),
  /** Nouvelle boutique du tableau de bord, sans compte Hiboutik. */
  z.object({
    mode: z.literal("new"),
    tenantId: z.string().min(1),
    organizationId: z.string().min(1),
    name: z.string().trim().min(1).max(120),
    city: z.string().trim().min(1).max(120),
    type: z.enum(["INTEGRATED", "FRANCHISE"]).default("FRANCHISE"),
  }),
]);

export const adminEasyshopSyncSchema = z.object({
  months: z.number().int().min(1).max(36).default(13),
});

export const adminEcritureSchema = z.object({
  magasin: z.number().int().positive(),
  reseau: z.union([
    z.object({ id: z.string().min(1) }),
    z.object({ nouveau: z.string().trim().min(2, "Nom du réseau : 2 caractères au moins").max(80) }),
  ]),
  proprietaire: z.object({
    email: z.string().trim().email("Adresse du propriétaire invalide"),
    nom: z.string().trim().max(80).nullish(),
  }),
  nomBoutique: z.string().trim().max(80).nullish(),
  reprendreSoldes: z.boolean().optional(),
  /** Clients créés en fiches réseau : vus de toutes les boutiques du réseau, un seul solde. */
  clientsReseau: z.boolean().optional(),
  corrections: z
    .object({
      produits: z.record(z.string(), z.object({ repris: z.boolean().optional(), unite: z.enum(["GRAMME", "PIECE"]).optional() })).optional(),
      clients: z.record(z.string(), z.boolean()).optional(),
      remises: z.record(z.string(), z.boolean()).optional(),
      vendeurs: z.record(z.string(), z.boolean()).optional(),
      clientsSansBoutique: z.boolean().optional(),
    })
    .nullish(),
  baremes: z
    .array(
      z.object({
        id: z.string().min(1),
        nom: z.string().trim().min(1, "Nom du barème requis").max(60),
        unite: z.enum(["GRAMME", "PIECE"]).optional(),
        mode: z.enum(["UNIT_PRICE", "DISCOUNT", "PACKET_PRICE"]).optional(),
        application: z.enum(["ALL_UNITS", "PACKET"]).optional(),
        paliers: z
          .array(
            z.object({
              seuil: z.number().int().positive(),
              prixCentimes: z.number().int().positive().nullish(),
              remiseBp: z.number().int().min(1).max(9999).nullish(),
            })
          )
          .min(1)
          .max(6),
        produits: z.array(z.number().int().positive()),
      })
    )
    .max(50)
    .nullish(),
});


// --- Page IT : suivi des livraisons ---

/** `propriétaire/dépôt`, ou l'adresse GitHub du dépôt (réduite à `propriétaire/dépôt`). */
export function normalizeRepo(input: string): string | null {
  const s = input
    .trim()
    .replace(/^https?:\/\/(www\.)?github\.com\//i, "")
    .replace(/\.git$/i, "")
    .replace(/\/+$/, "");
  return /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/.test(s) ? s : null;
}

const itRepo = z
  .string()
  .trim()
  .max(200)
  .transform((v, ctx) => {
    if (!v) return null;
    const r = normalizeRepo(v);
    if (!r) ctx.addIssue({ code: "custom", message: "Le dépôt doit ressembler à propriétaire/dépôt" });
    return r;
  });

export const itSettingsSchema = z.object({
  enabled: z.boolean().optional(),
  /** Jeton GitHub à enregistrer ; `null` pour l'effacer. */
  githubToken: z.union([z.string().trim().min(20, "Jeton trop court").max(400), z.null()]).optional(),
});

export const itProjectCreateSchema = z.object({
  name: z.string().trim().min(1, "Nom requis").max(80),
  repo: itRepo.optional(),
});
export const itProjectUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  repo: itRepo.optional(),
  track: z.boolean().optional(),
});

const itEntryBase = z.object({
  projectId: z.string().min(1),
  title: z.string().trim().min(1, "Titre requis").max(160),
  client: z.string().trim().max(120).nullish(),
  items: z.array(z.string().trim().min(1).max(300)).min(1, "Au moins une action").max(40),
  startDate: isoDay,
  endDate: isoDay.nullish(),
  status: z.enum(["PLANNED", "IN_PROGRESS", "DONE", "LIVE", "BLOCKED"]),
  note: z.string().trim().max(300).nullish(),
  /** Minutes ; 0 = pas d'estimation. */
  estimateMin: z.number().int().min(0).max(60 * 24 * 60).default(0),
});
export const itEntrySchema = itEntryBase.refine((v) => !v.endDate || v.endDate >= v.startDate, {
  message: "La fin ne peut pas précéder le début",
  path: ["endDate"],
});

export const itApiKeyCreateSchema = z.object({
  name: z.string().trim().min(1, "Nom requis").max(60),
  /** Lecture seule par défaut : l'assistant consulte, il ne modifie pas. */
  canWrite: z.boolean().default(false),
});
